import React, { useState, useEffect, useCallback } from 'react';
import emrAPI from '../../../api/emr';
import { patientAPI } from '../../../api/patient';
import { useToast } from '../../../hooks/useToast';

/**
 * Patient EMR Component
 * Provides comprehensive Electronic Medical Records access and management
 */
const PatientEMR = ({ loading: parentLoading, error: parentError, setError: setParentError }) => {
  const { showSuccess, showError, showInfo, showWarning } = useToast();
  const [loading, setLoading] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientSearchTerm, setPatientSearchTerm] = useState('');
  const [patientSearchResults, setPatientSearchResults] = useState([]);
  const [showPatientSearch, setShowPatientSearch] = useState(false);

  // EMR Data
  const [emrData, setEmrData] = useState(null);
  const [emrSummary, setEmrSummary] = useState(null);
  const [emrTimeline, setEmrTimeline] = useState([]);

  // View state
  const [activeView, setActiveView] = useState('summary'); // summary, timeline, records, create
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showAppointmentDetail, setShowAppointmentDetail] = useState(false);

  // Filter state
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [recordTypeFilter, setRecordTypeFilter] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [recordsPerPage] = useState(20);

  // Record Types
  const recordTypes = [
    { value: '', label: 'All Record Types' },
    { value: 'visit', label: 'Visit' },
    { value: 'appointment', label: 'Appointment' },
    { value: 'prescription', label: 'Prescription' },
    { value: 'lab_result', label: 'Lab Result' },
    { value: 'immunization', label: 'Immunization' },
    { value: 'vital_signs', label: 'Vital Signs' },
    { value: 'diagnosis', label: 'Diagnosis' },
    { value: 'procedure', label: 'Procedure' },
    { value: 'allergy', label: 'Allergy' },
    { value: 'clinical_note', label: 'Clinical Note' }
  ];

  // Search patients
  const handlePatientSearch = useCallback(async (searchTerm) => {
    if (!searchTerm.trim()) {
      setPatientSearchResults([]);
      setShowPatientSearch(false);
      return;
    }

    setLoading(true);
    try {
      const response = await patientAPI.searchPatients(searchTerm);
      const patients = response.data?.patients || [];

      setPatientSearchResults(patients);
      setShowPatientSearch(true);

      if (patients.length === 0) {
        showInfo('No patients found matching your search');
      } else {
        showInfo(`Found ${patients.length} patient${patients.length > 1 ? 's' : ''}`);
      }
    } catch (error) {
      console.error('Patient search failed:', error);
      showError('Failed to search patients');
      setPatientSearchResults([]);
      setShowPatientSearch(false);
    } finally {
      setLoading(false);
    }
  }, [showInfo, showError]);

  // Select patient
  const handleSelectPatient = async (patient) => {
    setSelectedPatient(patient);
    setShowPatientSearch(false);
    setPatientSearchTerm(`${patient.firstName} ${patient.lastName} - ${patient.mobile}`);

    // Load comprehensive EMR data which includes summary
    await loadPatientEMR(patient._id || patient.id);

    // Set active view to summary
    setActiveView('summary');
  };

  // Load patient EMR records - this also loads summary from the comprehensive endpoint
  const loadPatientEMR = async (patientId, params = {}) => {
    setLoading(true);
    try {
      const response = await emrAPI.getPatientEMR(patientId, {
        page: currentPage,
        limit: recordsPerPage,
        dateFrom,
        dateTo,
        recordTypes: recordTypeFilter,
        ...params
      });

      if (response.success) {
        setEmrData(response.data);
        // The comprehensive endpoint also returns summary
        if (response.data?.summary) {
          setEmrSummary(response.data.summary);
        }
        setTotalPages(response.data?.pagination?.pages || 1);
        const recordCount = response.data?.records?.length || 0;
        if (recordCount > 0) {
          showSuccess(`Loaded ${recordCount} EMR record${recordCount > 1 ? 's' : ''}`);
        }
      } else {
        showError('Failed to load patient EMR');
      }
    } catch (error) {
      console.error('Failed to load patient EMR:', error);
      showError('Failed to load patient EMR');
      setEmrData(null);
    } finally {
      setLoading(false);
    }
  };

  // Load patient summary separately if needed
  const loadPatientSummary = async (patientId) => {
    setLoading(true);
    try {
      const response = await emrAPI.getPatientSummary(patientId);

      if (response.success) {
        setEmrSummary(response.data);
        showSuccess('Patient EMR summary loaded');
      } else {
        showError('Failed to load patient summary');
      }
    } catch (error) {
      console.error('Failed to load patient summary:', error);
      showError('Failed to load patient summary');
      setEmrSummary(null);
    } finally {
      setLoading(false);
    }
  };

  // Load patient timeline
  const loadPatientTimeline = async (patientId, params = {}) => {
    setLoading(true);
    try {
      const response = await emrAPI.getPatientTimeline(patientId, {
        limit: 100,
        dateFrom,
        dateTo,
        ...params
      });

      if (response.success) {
        setEmrTimeline(response.data?.timeline || []);
        if (response.data?.timeline?.length > 0) {
          showSuccess(`Loaded ${response.data.timeline.length} timeline event${response.data.timeline.length > 1 ? 's' : ''}`);
        }
      } else {
        showError('Failed to load patient timeline');
      }
    } catch (error) {
      console.error('Failed to load patient timeline:', error);
      showError('Failed to load patient timeline');
      setEmrTimeline([]);
    } finally {
      setLoading(false);
    }
  };

  // Consolidate patient data
  const handleConsolidateData = async () => {
    if (!selectedPatient) return;

    if (!window.confirm('This will consolidate all existing patient data into EMR format. Continue?')) {
      return;
    }

    setLoading(true);
    try {
      const response = await emrAPI.consolidatePatientData(selectedPatient._id || selectedPatient.id);

      if (response.success) {
        const { appointments, visits, prescriptions, notes, immunizations } = response.data;
        showSuccess(`Consolidated: ${appointments || 0} appointments, ${visits || 0} visits, ${prescriptions || 0} prescriptions, ${notes || 0} notes, ${immunizations || 0} immunizations`);

        // Reload comprehensive EMR data
        await loadPatientEMR(selectedPatient._id || selectedPatient.id);
      } else {
        showError('Failed to consolidate patient data');
      }
    } catch (error) {
      console.error('Failed to consolidate patient data:', error);
      showError('Failed to consolidate patient data');
    } finally {
      setLoading(false);
    }
  };

  // Handle view change
  const handleViewChange = async (view) => {
    setActiveView(view);

    if (!selectedPatient) return;

    const patientId = selectedPatient._id || selectedPatient.id;

    switch (view) {
      case 'summary':
        // Summary is already loaded from the comprehensive endpoint
        // But reload if not available
        if (!emrSummary) {
          await loadPatientEMR(patientId);
        }
        break;
      case 'timeline':
        await loadPatientTimeline(patientId);
        break;
      case 'records':
        await loadPatientEMR(patientId);
        break;
      default:
        break;
    }
  };

  // Apply filters
  const handleApplyFilters = () => {
    if (!selectedPatient) return;

    const patientId = selectedPatient._id || selectedPatient.id;

    if (activeView === 'records') {
      setCurrentPage(1);
      loadPatientEMR(patientId);
    } else if (activeView === 'timeline') {
      loadPatientTimeline(patientId);
    }
  };

  // Render appointment detail modal
  const renderAppointmentDetail = () => {
    if (!selectedAppointment) return null;

    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
          {/* Background overlay */}
          <div
            className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
            onClick={() => {
              setShowAppointmentDetail(false);
              setSelectedAppointment(null);
            }}
          ></div>

          {/* Modal panel */}
          <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
            {/* Header */}
            <div className="bg-blue-600 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-white">
                Appointment Details
              </h3>
              <button
                onClick={() => {
                  setShowAppointmentDetail(false);
                  setSelectedAppointment(null);
                }}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="px-6 py-4 max-h-[calc(100vh-200px)] overflow-y-auto">
              {/* Basic Info */}
              <div className="mb-6 grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-gray-500">Doctor</div>
                  <div className="font-semibold text-gray-900">
                    Dr. {selectedAppointment.doctor?.firstName} {selectedAppointment.doctor?.lastName}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Date & Time</div>
                  <div className="font-semibold text-gray-900">
                    {new Date(selectedAppointment.dateTime).toLocaleString()}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Status</div>
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${selectedAppointment.status === 'Completed' ? 'bg-green-100 text-green-800' :
                      selectedAppointment.status === 'InProgress' ? 'bg-yellow-100 text-yellow-800' :
                        selectedAppointment.status === 'Cancelled' ? 'bg-red-100 text-red-800' :
                          'bg-blue-100 text-blue-800'
                    }`}>
                    {selectedAppointment.status}
                  </span>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Duration</div>
                  <div className="font-semibold text-gray-900">{selectedAppointment.durationMin} minutes</div>
                </div>
              </div>

              {/* Complaint */}
              {selectedAppointment.complaint && (
                <div className="mb-6 p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="text-sm font-medium text-gray-700 mb-1">Chief Complaint</div>
                  <div className="text-gray-900">{selectedAppointment.complaint}</div>
                </div>
              )}

              {/* Vitals */}
              {selectedAppointment.vitals && (
                <div className="mb-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">Vital Signs</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {selectedAppointment.vitals.bpSys && selectedAppointment.vitals.bpDia && (
                      <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                        <div className="text-xs text-gray-600 uppercase font-medium">Blood Pressure</div>
                        <div className="text-lg font-bold text-gray-900 mt-1">
                          {selectedAppointment.vitals.bpSys}/{selectedAppointment.vitals.bpDia}
                        </div>
                        <div className="text-xs text-gray-500">mmHg</div>
                      </div>
                    )}
                    {selectedAppointment.vitals.pulse && (
                      <div className="p-3 bg-pink-50 rounded-lg border border-pink-200">
                        <div className="text-xs text-gray-600 uppercase font-medium">Pulse</div>
                        <div className="text-lg font-bold text-gray-900 mt-1">{selectedAppointment.vitals.pulse}</div>
                        <div className="text-xs text-gray-500">bpm</div>
                      </div>
                    )}
                    {selectedAppointment.vitals.temperature && (
                      <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                        <div className="text-xs text-gray-600 uppercase font-medium">Temperature</div>
                        <div className="text-lg font-bold text-gray-900 mt-1">{selectedAppointment.vitals.temperature}°F</div>
                      </div>
                    )}
                    {selectedAppointment.vitals.oxygenSaturation && (
                      <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="text-xs text-gray-600 uppercase font-medium">SpO2</div>
                        <div className="text-lg font-bold text-gray-900 mt-1">{selectedAppointment.vitals.oxygenSaturation}%</div>
                      </div>
                    )}
                    {selectedAppointment.vitals.respiration && (
                      <div className="p-3 bg-teal-50 rounded-lg border border-teal-200">
                        <div className="text-xs text-gray-600 uppercase font-medium">Respiration</div>
                        <div className="text-lg font-bold text-gray-900 mt-1">{selectedAppointment.vitals.respiration}</div>
                        <div className="text-xs text-gray-500">breaths/min</div>
                      </div>
                    )}
                    {selectedAppointment.vitals.sugar && (
                      <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                        <div className="text-xs text-gray-600 uppercase font-medium">Blood Sugar</div>
                        <div className="text-lg font-bold text-gray-900 mt-1">{selectedAppointment.vitals.sugar}</div>
                        <div className="text-xs text-gray-500">mg/dL</div>
                      </div>
                    )}
                    {selectedAppointment.vitals.heightCm && (
                      <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                        <div className="text-xs text-gray-600 uppercase font-medium">Height</div>
                        <div className="text-lg font-bold text-gray-900 mt-1">{selectedAppointment.vitals.heightCm}</div>
                        <div className="text-xs text-gray-500">cm</div>
                      </div>
                    )}
                    {selectedAppointment.vitals.weightKg && (
                      <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                        <div className="text-xs text-gray-600 uppercase font-medium">Weight</div>
                        <div className="text-lg font-bold text-gray-900 mt-1">{selectedAppointment.vitals.weightKg}</div>
                        <div className="text-xs text-gray-500">kg</div>
                      </div>
                    )}
                    {selectedAppointment.vitals.bmi && (
                      <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                        <div className="text-xs text-gray-600 uppercase font-medium">BMI</div>
                        <div className="text-lg font-bold text-gray-900 mt-1">{selectedAppointment.vitals.bmi.toFixed(2)}</div>
                      </div>
                    )}
                    {selectedAppointment.vitals.pft && (
                      <div className="p-3 bg-cyan-50 rounded-lg border border-cyan-200">
                        <div className="text-xs text-gray-600 uppercase font-medium">PFT</div>
                        <div className="text-lg font-bold text-gray-900 mt-1">{selectedAppointment.vitals.pft}%</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Prescriptions */}
              {selectedAppointment.prescriptions && selectedAppointment.prescriptions.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">Prescriptions ({selectedAppointment.prescriptions.length})</h4>
                  <div className="space-y-4">
                    {selectedAppointment.prescriptions.map((prescription, index) => (
                      <div key={prescription._id || index} className="p-4 bg-green-50 rounded-lg border border-green-200">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <div className="font-semibold text-gray-900">
                              Prescription #{index + 1}
                            </div>
                            <div className="text-sm text-gray-600">
                              {new Date(prescription.createdAt).toLocaleString()}
                            </div>
                          </div>
                          {prescription.amount > 0 && (
                            <div className="text-lg font-bold text-green-700">
                              ₹{prescription.amount}
                            </div>
                          )}
                        </div>

                        {/* Medications */}
                        {prescription.items && prescription.items.length > 0 && (
                          <div className="mb-3">
                            <div className="text-sm font-medium text-gray-700 mb-2">Medications:</div>
                            <div className="space-y-2">
                              {prescription.items.map((item, idx) => (
                                <div key={item._id || idx} className="pl-4 border-l-3 border-green-500 bg-white p-3 rounded">
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <div className="font-medium text-gray-900">{item.name}</div>
                                      <div className="flex flex-wrap gap-3 mt-1 text-sm text-gray-600">
                                        {item.dose && <span>💊 Dose: {item.dose}</span>}
                                        {item.frequency && <span>🔄 Freq: {item.frequency}</span>}
                                      </div>
                                      {item.instruction && (
                                        <div className="text-sm text-gray-600 mt-1">
                                          📋 {item.instruction}
                                        </div>
                                      )}
                                      <div className="text-sm text-gray-600 mt-1">
                                        🗓️ Duration: {item.days} day{item.days > 1 ? 's' : ''}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Food Advice */}
                        {prescription.foodAdvice && (
                          <div className="p-3 bg-amber-50 rounded border border-amber-200">
                            <div className="text-sm font-medium text-gray-700">Food Advice:</div>
                            <div className="text-sm text-gray-900 mt-1">{prescription.foodAdvice}</div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Additional Info */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                {selectedAppointment.appointmentType && (
                  <div>
                    <div className="text-gray-500">Appointment Type</div>
                    <div className="font-medium text-gray-900">{selectedAppointment.appointmentType}</div>
                  </div>
                )}
                {selectedAppointment.arrivedAt && (
                  <div>
                    <div className="text-gray-500">Arrived At</div>
                    <div className="font-medium text-gray-900">
                      {new Date(selectedAppointment.arrivedAt).toLocaleTimeString()}
                    </div>
                  </div>
                )}
                {selectedAppointment.joinedQueueAt && (
                  <div>
                    <div className="text-gray-500">Joined Queue At</div>
                    <div className="font-medium text-gray-900">
                      {new Date(selectedAppointment.joinedQueueAt).toLocaleTimeString()}
                    </div>
                  </div>
                )}
                {selectedAppointment.queuePosition !== undefined && (
                  <div>
                    <div className="text-gray-500">Queue Position</div>
                    <div className="font-medium text-gray-900">{selectedAppointment.queuePosition}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-6 py-4 flex justify-end">
              <button
                onClick={() => {
                  setShowAppointmentDetail(false);
                  setSelectedAppointment(null);
                }}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render patient search
  const renderPatientSearch = () => (
    <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Select Patient</h2>

      <div className="relative">
        <input
          type="text"
          placeholder="Search by name, mobile, or ID..."
          value={patientSearchTerm}
          onChange={(e) => {
            setPatientSearchTerm(e.target.value);
            handlePatientSearch(e.target.value);
          }}
          className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />

        {showPatientSearch && patientSearchResults.length > 0 && (
          <div className="absolute z-10 w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-96 overflow-y-auto">
            {patientSearchResults.map((patient) => (
              <div
                key={patient._id || patient.id}
                onClick={() => handleSelectPatient(patient)}
                className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
              >
                <div className="font-medium text-gray-900">
                  {patient.firstName} {patient.lastName}
                </div>
                <div className="text-sm text-gray-500">
                  Mobile: {patient.mobile} | DOB: {patient.dob ? new Date(patient.dob).toLocaleDateString() : 'N/A'}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {selectedPatient && (
        <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium text-gray-900">
                {selectedPatient.firstName} {selectedPatient.lastName}
              </div>
              <div className="text-sm text-gray-600">
                ID: {selectedPatient._id || selectedPatient.id} | Mobile: {selectedPatient.mobile}
              </div>
              {(selectedPatient.policyNumber || selectedPatient.abhaNumber || selectedPatient.companyTpaId) && (
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs font-medium text-blue-800">
                  {selectedPatient.policyNumber && <span>🛡️ Policy: {selectedPatient.policyNumber}</span>}
                  {selectedPatient.companyTpaId && <span>🏥 TPA: {selectedPatient.companyTpaId}</span>}
                  {selectedPatient.abhaNumber && <span>🆔 ABHA: {selectedPatient.abhaNumber}</span>}
                </div>
              )}
            </div>
            <button
              onClick={() => {
                setSelectedPatient(null);
                setPatientSearchTerm('');
                setEmrSummary(null);
                setEmrData(null);
                setEmrTimeline([]);
              }}
              className="text-sm text-red-600 hover:text-red-700 font-medium"
            >
              Clear
            </button>
          </div>
        </div>
      )}
    </div>
  );

  // Render view tabs
  const renderViewTabs = () => {
    if (!selectedPatient) return null;

    const tabs = [
      { id: 'summary', label: 'Summary', icon: '📊' },
      { id: 'timeline', label: 'Timeline', icon: '📅' },
      { id: 'records', label: 'All Records', icon: '📋' }
    ];

    return (
      <div className="bg-white rounded-lg border border-gray-200 mb-6">
        <div className="flex border-b border-gray-200">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleViewChange(tab.id)}
              className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${activeView === tab.id
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        <div className="p-4 flex items-center justify-between bg-gray-50">
          <button
            onClick={handleConsolidateData}
            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors"
          >
            Consolidate Data
          </button>

          <div className="flex items-center space-x-4">
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
            <span className="text-gray-500">to</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />

            {activeView === 'records' && (
              <select
                value={recordTypeFilter}
                onChange={(e) => setRecordTypeFilter(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                {recordTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            )}

            <button
              onClick={handleApplyFilters}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
            >
              Apply Filters
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Render summary view
  const renderSummaryView = () => {
    if (!emrSummary) {
      return (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <div className="text-6xl mb-4">📊</div>
          <p className="text-gray-500">No EMR summary available</p>
        </div>
      );
    }

    const { recordCounts, recentVitals, activeDiagnoses, currentMedications, medicalHistory, lastAppointment, upcomingAppointments } = emrSummary;

    // Extract real data from the actual API response structure
    const {
      totalAppointments = 0,
      totalPrescriptions = 0,
      totalVisits = 0,
      totalNotes = 0,
      allAppointments = [],
      pastAppointments = [],
      upcomingAppointments: upcoming = [],
      drugAllergies = [],
      pastHistory
    } = emrSummary;

    // Get most recent appointment with vitals
    const recentAppointmentWithVitals = allAppointments.find(apt => apt.vitals);

    // Extract all prescriptions from appointments
    const allPrescriptionsList = allAppointments
      .filter(apt => apt.prescriptions && apt.prescriptions.length > 0)
      .flatMap(apt => apt.prescriptions);

    return (
      <div className="space-y-6">
        {/* Record Counts */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Record Statistics</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="text-2xl font-bold text-blue-600">{totalAppointments}</div>
              <div className="text-sm text-gray-600 mt-1">Total Appointments</div>
            </div>
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="text-2xl font-bold text-green-600">{totalPrescriptions}</div>
              <div className="text-sm text-gray-600 mt-1">Prescriptions</div>
            </div>
            <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
              <div className="text-2xl font-bold text-purple-600">{totalVisits}</div>
              <div className="text-sm text-gray-600 mt-1">Visits</div>
            </div>
            <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
              <div className="text-2xl font-bold text-orange-600">{totalNotes}</div>
              <div className="text-sm text-gray-600 mt-1">Clinical Notes</div>
            </div>
          </div>
        </div>

        {/* Recent Vitals */}
        {recentAppointmentWithVitals?.vitals && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Vital Signs</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {recentAppointmentWithVitals.vitals.bpSys && recentAppointmentWithVitals.vitals.bpDia && (
                <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                  <div className="text-xs text-gray-600 uppercase font-medium">Blood Pressure</div>
                  <div className="text-xl font-bold text-gray-900 mt-1">
                    {recentAppointmentWithVitals.vitals.bpSys}/{recentAppointmentWithVitals.vitals.bpDia}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">mmHg</div>
                </div>
              )}
              {recentAppointmentWithVitals.vitals.pulse && (
                <div className="p-3 bg-pink-50 rounded-lg border border-pink-200">
                  <div className="text-xs text-gray-600 uppercase font-medium">Pulse</div>
                  <div className="text-xl font-bold text-gray-900 mt-1">
                    {recentAppointmentWithVitals.vitals.pulse}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">bpm</div>
                </div>
              )}
              {recentAppointmentWithVitals.vitals.temperature && (
                <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                  <div className="text-xs text-gray-600 uppercase font-medium">Temperature</div>
                  <div className="text-xl font-bold text-gray-900 mt-1">
                    {recentAppointmentWithVitals.vitals.temperature}°F
                  </div>
                </div>
              )}
              {recentAppointmentWithVitals.vitals.oxygenSaturation && (
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="text-xs text-gray-600 uppercase font-medium">SpO2</div>
                  <div className="text-xl font-bold text-gray-900 mt-1">
                    {recentAppointmentWithVitals.vitals.oxygenSaturation}%
                  </div>
                </div>
              )}
              {recentAppointmentWithVitals.vitals.respiration && (
                <div className="p-3 bg-teal-50 rounded-lg border border-teal-200">
                  <div className="text-xs text-gray-600 uppercase font-medium">Respiration</div>
                  <div className="text-xl font-bold text-gray-900 mt-1">
                    {recentAppointmentWithVitals.vitals.respiration}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">breaths/min</div>
                </div>
              )}
              {recentAppointmentWithVitals.vitals.sugar && (
                <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                  <div className="text-xs text-gray-600 uppercase font-medium">Blood Sugar</div>
                  <div className="text-xl font-bold text-gray-900 mt-1">
                    {recentAppointmentWithVitals.vitals.sugar}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">mg/dL</div>
                </div>
              )}
              {recentAppointmentWithVitals.vitals.heightCm && (
                <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                  <div className="text-xs text-gray-600 uppercase font-medium">Height</div>
                  <div className="text-xl font-bold text-gray-900 mt-1">
                    {recentAppointmentWithVitals.vitals.heightCm}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">cm</div>
                </div>
              )}
              {recentAppointmentWithVitals.vitals.weightKg && (
                <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                  <div className="text-xs text-gray-600 uppercase font-medium">Weight</div>
                  <div className="text-xl font-bold text-gray-900 mt-1">
                    {recentAppointmentWithVitals.vitals.weightKg}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">kg</div>
                </div>
              )}
              {recentAppointmentWithVitals.vitals.bmi && (
                <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                  <div className="text-xs text-gray-600 uppercase font-medium">BMI</div>
                  <div className="text-xl font-bold text-gray-900 mt-1">
                    {recentAppointmentWithVitals.vitals.bmi.toFixed(2)}
                  </div>
                </div>
              )}
              {recentAppointmentWithVitals.vitals.pft && (
                <div className="p-3 bg-cyan-50 rounded-lg border border-cyan-200">
                  <div className="text-xs text-gray-600 uppercase font-medium">PFT</div>
                  <div className="text-xl font-bold text-gray-900 mt-1">
                    {recentAppointmentWithVitals.vitals.pft}%
                  </div>
                </div>
              )}
            </div>
            <div className="text-xs text-gray-500 mt-3">
              Recorded: {new Date(recentAppointmentWithVitals.dateTime).toLocaleString()}
            </div>
          </div>
        )}

        {/* Current Medications from Prescriptions */}
        {allPrescriptionsList && allPrescriptionsList.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Prescriptions</h3>
            <div className="space-y-4">
              {allPrescriptionsList.slice(0, 3).map((prescription, index) => (
                <div key={prescription._id || index} className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-start justify-between mb-2">
                    <div className="font-medium text-gray-900">
                      Dr. {prescription.doctor?.firstName} {prescription.doctor?.lastName}
                    </div>
                    <div className="text-xs text-gray-500">
                      {new Date(prescription.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                  {prescription.items && prescription.items.length > 0 && (
                    <div className="space-y-2">
                      {prescription.items.map((item, idx) => (
                        <div key={item._id || idx} className="pl-3 border-l-2 border-green-400">
                          <div className="font-medium text-sm text-gray-900">{item.name}</div>
                          <div className="text-xs text-gray-600">
                            {item.instruction} - {item.days} days
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {prescription.foodAdvice && (
                    <div className="mt-2 text-sm text-gray-600">
                      <span className="font-medium">Food Advice:</span> {prescription.foodAdvice}
                    </div>
                  )}
                  {prescription.amount > 0 && (
                    <div className="mt-2 text-sm font-medium text-gray-900">
                      Amount: ₹{prescription.amount}
                    </div>
                  )}
                </div>
              ))}
              {allPrescriptionsList.length > 3 && (
                <div className="text-center text-sm text-blue-600 cursor-pointer hover:text-blue-700">
                  View {allPrescriptionsList.length - 3} more prescription{allPrescriptionsList.length - 3 > 1 ? 's' : ''}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Active Diagnoses */}
        {activeDiagnoses && activeDiagnoses.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Active Diagnoses</h3>
            <div className="space-y-2">
              {activeDiagnoses.map((diagnosis, index) => (
                <div key={index} className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                  <div className="font-medium text-gray-900">{diagnosis.display || diagnosis.code}</div>
                  <div className="text-sm text-gray-600">Severity: {diagnosis.severity || 'N/A'}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Current Medications */}
        {currentMedications && currentMedications.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Current Medications</h3>
            <div className="space-y-2">
              {currentMedications.map((med, index) => (
                <div key={index} className="p-3 bg-green-50 rounded-lg border border-green-200">
                  <div className="font-medium text-gray-900">{med.name}</div>
                  <div className="text-sm text-gray-600">
                    {med.dosage} - {med.frequency} ({med.route})
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Medical History */}
        {medicalHistory && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Medical History</h3>

            {medicalHistory.allergies && medicalHistory.allergies.length > 0 && (
              <div className="mb-4">
                <h4 className="font-medium text-gray-700 mb-2">Allergies</h4>
                <div className="flex flex-wrap gap-2">
                  {medicalHistory.allergies.map((allergy, index) => (
                    <span key={index} className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm">
                      {allergy}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {medicalHistory.chronicConditions && medicalHistory.chronicConditions.length > 0 && (
              <div className="mb-4">
                <h4 className="font-medium text-gray-700 mb-2">Chronic Conditions</h4>
                <div className="space-y-2">
                  {medicalHistory.chronicConditions.map((condition, index) => (
                    <div key={index} className="p-3 bg-gray-50 rounded-lg">
                      <div className="font-medium text-gray-900">{condition.condition}</div>
                      <div className="text-sm text-gray-600">
                        Diagnosed: {new Date(condition.diagnosedDate).toLocaleDateString()}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {medicalHistory.bloodType && (
              <div>
                <h4 className="font-medium text-gray-700 mb-2">Blood Type</h4>
                <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold">
                  {medicalHistory.bloodType}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Appointments */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {pastAppointments && pastAppointments.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Appointments</h3>
              <div className="space-y-3">
                {pastAppointments.slice(0, 3).map((apt) => (
                  <div
                    key={apt._id}
                    className="p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 cursor-pointer transition-colors"
                    onClick={() => {
                      setSelectedAppointment(apt);
                      setShowAppointmentDetail(true);
                    }}
                  >
                    <div className="font-medium text-gray-900">
                      Dr. {apt.doctor?.firstName} {apt.doctor?.lastName}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      {new Date(apt.dateTime).toLocaleString()}
                    </div>
                    <div className="text-sm text-gray-600">
                      Status: <span className={`px-2 py-1 rounded-full text-xs font-medium ${apt.status === 'Completed' ? 'bg-green-100 text-green-800' :
                          apt.status === 'InProgress' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-blue-100 text-blue-800'
                        }`}>{apt.status}</span>
                    </div>
                    {apt.complaint && (
                      <div className="text-sm text-gray-600 mt-1">
                        Complaint: {apt.complaint}
                      </div>
                    )}
                    <div className="text-xs text-blue-600 mt-2">Click to view full details →</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {upcoming && upcoming.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Upcoming Appointments</h3>
              <div className="space-y-3">
                {upcoming.slice(0, 3).map((apt) => (
                  <div
                    key={apt._id}
                    className="p-3 bg-blue-50 rounded-lg border border-blue-200 hover:bg-blue-100 cursor-pointer transition-colors"
                    onClick={() => {
                      setSelectedAppointment(apt);
                      setShowAppointmentDetail(true);
                    }}
                  >
                    <div className="font-medium text-gray-900">
                      Dr. {apt.doctor?.firstName || apt.doctor?.name || 'N/A'} {apt.doctor?.lastName || ''}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      {new Date(apt.dateTime).toLocaleString()}
                    </div>
                    {apt.note && (
                      <div className="text-sm text-gray-600 mt-1">
                        Note: {apt.note}
                      </div>
                    )}
                    <div className="text-xs text-blue-600 mt-2">Click to view full details →</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render timeline view
  const renderTimelineView = () => {
    if (!emrTimeline || emrTimeline.length === 0) {
      return (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <div className="text-6xl mb-4">📅</div>
          <p className="text-gray-500">No timeline events available</p>
        </div>
      );
    }

    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Medical Timeline</h3>
        <div className="space-y-4">
          {emrTimeline.map((event) => (
            <div key={event.id} className="flex">
              <div className="flex-shrink-0 w-24 text-sm text-gray-500">
                {new Date(event.date).toLocaleDateString()}
              </div>
              <div className="flex-shrink-0 w-2 bg-blue-200 rounded-full mx-4"></div>
              <div className="flex-1 pb-8">
                <div className={`p-4 rounded-lg border ${event.priority === 'critical' ? 'bg-red-50 border-red-200' :
                    event.priority === 'high' ? 'bg-orange-50 border-orange-200' :
                      'bg-gray-50 border-gray-200'
                  }`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{event.title}</div>
                      <div className="text-sm text-gray-600 mt-1">{event.summary}</div>
                      {event.physician && (
                        <div className="text-sm text-gray-500 mt-2">
                          Physician: {event.physician.name}
                        </div>
                      )}
                    </div>
                    <span className="ml-4 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                      {event.type}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render records view
  const renderRecordsView = () => {
    if (!emrData || !emrData.records || emrData.records.length === 0) {
      return (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <div className="text-6xl mb-4">📋</div>
          <p className="text-gray-500">No EMR records available</p>
        </div>
      );
    }

    return (
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            All EMR Records ({emrData.pagination?.total || 0})
          </h3>

          <div className="space-y-3">
            {emrData.records.map((record) => (
              <div
                key={record._id}
                className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                onClick={() => setSelectedRecord(record)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full font-medium">
                        {record.recordType}
                      </span>
                      <span className="text-sm text-gray-500">
                        {new Date(record.recordDate || record.createdAt).toLocaleString()}
                      </span>
                    </div>

                    {record.chiefComplaint && (
                      <div className="mt-2 text-sm text-gray-900 font-medium">
                        {record.chiefComplaint}
                      </div>
                    )}

                    {record.clinicalNotes && (
                      <div className="mt-1 text-sm text-gray-600 line-clamp-2">
                        {record.clinicalNotes}
                      </div>
                    )}

                    {record.attendingPhysician && (
                      <div className="mt-2 text-sm text-gray-500">
                        Physician: Dr. {record.attendingPhysician.name || 'N/A'}
                      </div>
                    )}
                  </div>

                  {record.priority && (
                    <span className={`ml-4 px-2 py-1 text-xs rounded-full ${record.priority === 'critical' ? 'bg-red-100 text-red-800' :
                        record.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                          'bg-gray-100 text-gray-800'
                      }`}>
                      {record.priority}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-between">
            <button
              onClick={() => {
                setCurrentPage(prev => Math.max(1, prev - 1));
                loadPatientEMR(selectedPatient._id || selectedPatient.id);
              }}
              disabled={currentPage === 1}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>

            <span className="text-sm text-gray-600">
              Page {currentPage} of {totalPages}
            </span>

            <button
              onClick={() => {
                setCurrentPage(prev => Math.min(totalPages, prev + 1));
                loadPatientEMR(selectedPatient._id || selectedPatient.id);
              }}
              disabled={currentPage === totalPages}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex-1 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Patient Electronic Medical Records</h1>
          <p className="text-gray-600">View comprehensive patient medical history and records</p>
        </div>

        {/* Patient Search */}
        {renderPatientSearch()}

        {/* View Tabs */}
        {renderViewTabs()}

        {/* Content */}
        {loading ? (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-500">Loading EMR data...</p>
          </div>
        ) : (
          <>
            {activeView === 'summary' && renderSummaryView()}
            {activeView === 'timeline' && renderTimelineView()}
            {activeView === 'records' && renderRecordsView()}
          </>
        )}

        {/* Appointment Detail Modal */}
        {showAppointmentDetail && renderAppointmentDetail()}
      </div>
    </div>
  );
};

export default PatientEMR;
